/* Perfumzy storefront safeguard. Checkout enforcement requires server validation. */
(() => {
  'use strict';
  const config = JSON.parse(document.getElementById('BundleOnlyConfig').textContent);
  const handles = new Set(config.handles);
  const root = window.Shopify?.routes?.root || '/';
  const message = 'These products can only be purchased as part of a bundle.';
  let busy = false;
  const protectedItem = item => handles.has(item.handle) || Boolean(item.properties?._bundle_id) || item.properties?._bundle_type === 'pick3';
  function valid(cart) {
    const groups = new Map();
    for (const item of cart.items) {
      if (!protectedItem(item)) continue;
      const id = item.properties?._bundle_id;
      if (!handles.has(item.handle) || !id || item.properties?._bundle_type !== 'pick3' || item.quantity !== 1) return false;
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(item);
    }
    return [...groups.values()].every(items => items.length === 3 && new Set(items.map(item => item.product_id)).size === 3);
  }
  async function json(path, body) {
    const response = await fetch(root + path, body ? {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    } : { cache: 'no-store' });
    const result = await response.json();
    if (!response.ok || result.status >= 400) throw new Error(result.description || 'Unable to update your cart. Please try again.');
    return result;
  }
  function notice(text = message) {
    let box = document.getElementById('BundleOnlyNotice');
    if (!box) {
      box = document.createElement('div');
      box.id = 'BundleOnlyNotice';
      box.className = 'bundle-only-notice';
      box.setAttribute('role', 'alert');
      (document.querySelector('cart-drawer[open] .drawer__content') || document.querySelector('cart-drawer[open]') || document.getElementById('MainContent')).prepend(box);
    }
    box.replaceChildren(document.createTextNode(text + ' '));
    const link = document.createElement('a');
    link.href = config.url; link.textContent = 'Build your bundle'; link.className = 'link';
    box.append(link);
    box.scrollIntoView({ block: 'nearest' });
  }
  function decorate() {
    const seen = new Map();
    document.querySelectorAll('[data-bundle-line]').forEach(row => {
      if (!handles.has(row.dataset.productHandle) && !row.dataset.bundleId) return;
      row.setAttribute('data-bundle-protected', '');
      row.querySelectorAll('quantity-input button, input.quantity__input').forEach(input => { input.disabled = true; });
      const remove = row.querySelector('[is="cart-remove-button"]');
      if (remove && remove.textContent !== 'Remove bundle' && row.dataset.bundleId) remove.textContent = 'Remove bundle';
      const container = row.closest('cart-items, cart-drawer') || row.parentElement;
      if (!seen.has(container)) seen.set(container, new Set());
      const group = row.dataset.bundleId;
      const first = group && !seen.get(container).has(group);
      seen.get(container).add(group);
      let label = row.querySelector('.bundle-only-label');
      if (!label) {
        label = document.createElement('span'); label.className = 'bundle-only-label';
        (row.querySelector('.horizontal-product__title, .cart-item__name')?.parentElement || row.querySelector('td') || row).prepend(label);
      }
      const text = group ? (first ? 'Your bundle · 3 fragrances' : 'Bundle fragrance') : message;
      if (label.textContent !== text) label.textContent = text;
    });
    // Covers quick add and quick view forms, including dynamically rendered forms.
    document.querySelectorAll('form[is="product-form"]').forEach(form => {
      const id = form.querySelector('[name="id"]')?.value;
      if (!config.variants[id]) return;
      form.querySelectorAll('[name="add"]').forEach(button => { if (button.textContent !== 'Build your bundle') button.textContent = 'Build your bundle'; });
      form.querySelectorAll('.shopify-payment-button').forEach(el => { el.hidden = true; });
    });
  }
  async function checkout(proceed) {
    if (busy) return;
    busy = true;
    try {
      const cart = await json('cart.js');
      if (!valid(cart)) { notice(); return; }
      proceed();
    } catch (error) { notice(error.message); }
    finally { busy = false; }
  }
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-product-bundle-submit]');
    if (button) {
      const bundle = button.closest('product-bundle, compact-product-bundle');
      if (!bundle) return;
      const selected = bundle.matches('product-bundle')
        ? [...bundle.querySelectorAll('[data-product-bundle-variant]:not([available])')].map(row => ({ id: row.dataset.variantId, quantity: Number(row.querySelector('quantity-input input')?.value || 1) }))
        : [...bundle.querySelectorAll('form[is="product-bundle-form"] [name="id"]')].map(input => ({ id: input.value, quantity: Number(input.dataset.min || 1) }));
      // Other Concept compact bundles remain on their native flow.
      if (bundle.matches('compact-product-bundle') && !selected.some(item => config.variants[item.id])) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (button.disabled || busy) return;
      if (selected.length !== 3 || selected.some(item => !config.variants[item.id] || item.quantity !== 1) || new Set(selected.map(item => config.variants[item.id])).size !== 3) {
        notice('Please choose exactly 3 different fragrances, with one of each.'); return;
      }
      busy = true; button.setAttribute('aria-busy', 'true');
      try {
        const id = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        await json('cart/add.js', { items: selected.map(item => ({ ...item, properties: { _bundle_id: id, _bundle_type: 'pick3' } })) });
        const cart = await json('cart.js');
        if (!valid(cart)) { window.location.assign(root + 'cart'); return; }
        window.location.assign(button.hasAttribute('data-buy-now') ? root + 'checkout' : root + 'cart');
      } catch (error) { notice(error.message); }
      finally { busy = false; button.removeAttribute('aria-busy'); }
      return;
    }
    const remove = event.target.closest('[is="cart-remove-button"]');
    const row = remove?.closest('[data-bundle-protected]');
    if (row) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (busy) return;
      busy = true;
      try {
        const cart = await json('cart.js');
        const updates = {};
        cart.items.filter(item => row.dataset.bundleId ? item.properties?._bundle_id === row.dataset.bundleId : item.key === row.dataset.lineKey).forEach(item => { updates[item.key] = 0; });
        await json('cart/update.js', { updates });
        window.location.reload();
      } catch (error) { notice(error.message); }
      finally { busy = false; }
      return;
    }
    const form = event.target.closest('form[is="product-form"]');
    if (form && config.variants[form.querySelector('[name="id"]')?.value] && event.target.closest('[name="add"], .shopify-payment-button')) {
      event.preventDefault(); event.stopImmediatePropagation(); window.location.assign(config.url); return;
    }
    const target = event.target.closest('[name="checkout"], a[href]');
    if (!target) return;
    const href = target.getAttribute('href');
    if (!target.matches('[name="checkout"]') && !/\/checkout(?:[/?#]|$)/.test(href || '')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    checkout(() => window.location.assign(href || root + 'checkout'));
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (form.matches('[is="product-form"]') && config.variants[form.querySelector('[name="id"]')?.value]) {
      event.preventDefault(); event.stopImmediatePropagation(); window.location.assign(config.url); return;
    }
    if (!/\/cart\/?$/.test(new URL(form.action, location.href).pathname) || (event.submitter && event.submitter.name !== 'checkout')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    checkout(() => window.location.assign(root + 'checkout'));
  }, true);
  new MutationObserver(decorate).observe(document.body, { childList: true, subtree: true });
  decorate();
  async function checkReturn() {
    try { if (!valid(await json('cart.js'))) notice(); } catch (error) { console.warn('Bundle cart check:', error); }
    decorate();
  }
  window.addEventListener('pageshow', checkReturn);
  document.addEventListener('cart:refresh', checkReturn);
  document.addEventListener('ajaxProduct:added', checkReturn);
  // Exposed for theme integrations and local validation tests.
  window.PerfumzyBundle = { valid };
})();
